import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { REGISTRATION_SOURCE } from '@/common/functions/getUtms';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import LanguageSwitcher from '@/common/components/languageSwitcher/LanguageSwitcher';
import Skeleton from '@/common/components/skeleton/Skeleton';
import BuyTokensModal from './components/BuyTokensModal/BuyTokensModal';
import PlanCheckoutModal from '@/common/components/paywall/PlanCheckoutModal';
import ProfileMenu from './components/ProfileMenu/ProfileMenu';
import { useProductContext } from '../../ProductContext';
import { useAccountPlanGate } from '@/common/hooks/useAccountPlanGate';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { STUDIO_BRAND_COIN, STUDIO_BRAND_LOGO } from '@/common/constants/studioPublicMedia';
import { getProfileDisplay } from '@/common/utils/profileDisplay';
import {
	Account,
	Brand,
	CoinBalance,
	CoinIcon,
	CoinMenu,
	CoinMenuBalance,
	CoinMenuHead,
	CoinMenuLabel,
	Coins,
	GuestActionButton,
	GuestActions,
	Topbar,
	TopbarLanguageSlot,
	TopbarLeft,
} from './TopBar.styles';

const TopBar = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const currentReturnPath = `${location.pathname}${location.search}`;
	const common = useLanguageData('common');
	const t = useLanguageData('topBar');
	const { tokenCount, isLoading } = useProductContext();
	const {
		isGuest,
		user,
		isAuthLoading,
		openAuthModal,
		logout,
		isLoggingOut,
		updateUser,
	} = useGuestAuth();
	const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
	const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
	const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
	const { isAccountActive } = useAccountPlanGate(!isGuest && Boolean(user));

	const userData = useMemo(
		() => ({
			name: user?.name ?? '',
			email: user?.email ?? '',
			joinedDate: user?.joinedDate ?? null,
			planExpiry: user?.planExpiry ?? null,
		}),
		[user],
	);
	const { initial: userInitial } = useMemo(() => getProfileDisplay(user), [user]);

	const handleLogout = () => {
		logout(() => {
			setIsProfileMenuOpen(false);
			navigate('/');
		});
	};

	const openSparkModal = () => {
		if (isAccountActive === true) {
			setIsTokenModalOpen(true);
			return;
		}

		setIsPlanModalOpen(true);
	};

	const handleNameUpdated = (profile) => {
		updateUser({ name: profile?.name });
	};

	return (
		<>
			<Topbar>
				<TopbarLeft>
					<Brand
						type="button"
						aria-label={common.appTitle}
						onClick={() => navigate('/')}
					>
						<img src={STUDIO_BRAND_LOGO} alt={common.appTitle} />
					</Brand>
				</TopbarLeft>
				<Account>
					{isGuest ? (
						<>
							<GuestActions>
								<GuestActionButton
									type="button"
									onClick={() => openAuthModal('login', currentReturnPath)}
								>
									{t.logIn}
								</GuestActionButton>
								<GuestActionButton
									type="button"
									$primary
									onClick={() =>
									openAuthModal(
										'register',
										currentReturnPath,
										REGISTRATION_SOURCE.TOP,
									)
								}
								>
									{t.register}
								</GuestActionButton>
							</GuestActions>
							<TopbarLanguageSlot>
								<LanguageSwitcher />
							</TopbarLanguageSlot>
						</>
					) : (
						<>
							<Coins
								id="sparkBalance"
								role="button"
								tabIndex={0}
								title={t.sparkBalance}
								aria-label={t.openSparkPackages}
								onClick={openSparkModal}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										openSparkModal();
									}
								}}
							>
								<CoinBalance id="coinBalance">
									{isLoading ? common.loading : tokenCount}
								</CoinBalance>
								<CoinIcon src={STUDIO_BRAND_COIN} alt="" aria-hidden="true" />
								<CoinMenu role="menu" aria-label={t.sparkBalance}>
									<CoinMenuHead>
										<CoinMenuBalance>
											<img src={STUDIO_BRAND_COIN} alt="" aria-hidden="true" />
											<strong>{isLoading ? common.loading : tokenCount}</strong>
										</CoinMenuBalance>
										<CoinMenuLabel>{t.totalBalance}</CoinMenuLabel>
									</CoinMenuHead>
								</CoinMenu>
							</Coins>
							{isAuthLoading ? (
								<Skeleton
									width="34px"
									height="34px"
									radius="50%"
									minWidth="34px"
									minHeight="34px"
								/>
							) : (
								<ProfileMenu
									open={isProfileMenuOpen}
									onToggle={() => setIsProfileMenuOpen((prev) => !prev)}
									onClose={() => setIsProfileMenuOpen(false)}
									userInitial={userInitial}
									userData={userData}
									tokenCount={tokenCount}
									onLogout={handleLogout}
									isLoggingOut={isLoggingOut}
									onNameUpdated={handleNameUpdated}
								/>
							)}
							<TopbarLanguageSlot>
								<LanguageSwitcher />
							</TopbarLanguageSlot>
						</>
					)}
				</Account>
			</Topbar>

			{isPlanModalOpen && (
				<PlanCheckoutModal
					returnPath={currentReturnPath}
					onClose={() => setIsPlanModalOpen(false)}
				/>
			)}
			{isTokenModalOpen && (
				<BuyTokensModal
					returnPath={currentReturnPath}
					onClose={() => setIsTokenModalOpen(false)}
				/>
			)}
		</>
	);
};

export default TopBar;
